import React from 'react';
import ExtractCtrl from '../svc/ExtractCtrl';
import {SOURCES_CONFIG} from '../constants/sources';
import {
    DPE_GRADE_TO_NUM,
    DPE_NUM_TO_GRADE,
    computeMedianGrade,
    computeMeanGrade,
} from '../utils/dpeGradeUtils';
import {computeMetaColumns} from '../utils/metaColumnsUtils';

/**
 * ⚠️  Passer à `false` avant de déployer.
 * En mode dev, charge uniquement le premier lot (1 enregistrement) par source.
 */
const DEV_LIMIT = false;
const LIMIT = 500;

/**
 * Hook gérant l'intégralité du cycle de vie du chargement des données :
 * appels paginés aux API, fusion des sources par adresse, et état de progression.
 *
 * @param {Object} filter  - Paramètres de filtrage (geo, typesDeBiens, …)
 * @param {string} geoMode - Clé pivot pour l'agrégation (geom_groupe|geom_cstr|latlon|geom_adresse|geom_parcelle)
 * @returns {{
 *   csvData: Object|null,
 *   loading: boolean,
 *   processing: boolean,
 *   tableReady: boolean,
 *   sourcesWithState: Array
 * }}
 */
export function useExtractData(filter, geoMode = 'geom_groupe') {

    // ── État d'erreur ─────────────────────────────────────────────────────────
    const [error, setError] = React.useState(null);

    // ── États de chargement ───────────────────────────────────────────────────
    const [loading, setLoading] = React.useState(false);
    const [loadingAdresses, setLoadingAdresses] = React.useState(-1);
    const [loadingBatimentGroupe, setLoadingBatimentGroupe] = React.useState(-1);
    const [loadingBatiments, setLoadingBatiments] = React.useState(-1);
    const [loadingBatimentGroupeRnc, setLoadingBatimentGroupeRnc] = React.useState(-1);
    const [loadingProp, setLoadingProp] = React.useState(-1);
    const [loadingParcelles, setLoadingParcelles] = React.useState(-1);
    const [loadingDpes, setLoadingDpes] = React.useState(-1);
    const [loadingEtab, setLoadingEtab] = React.useState(-1);
    const [loadingBatimentGroupeAdresse, setLoadingBatimentGroupeAdresse] = React.useState(-1);
    const [loadingBatimentConstruction, setLoadingBatimentConstruction] = React.useState(-1);
    const [loadingProprietaires, setLoadingProprietaires] = React.useState(-1);
    const [loadingRnc, setLoadingRnc] = React.useState(-1);
    const [processing, setProcessing] = React.useState(false);
    const [tableReady, setTableReady] = React.useState(false);

    // ── Données brutes ────────────────────────────────────────────────────────
    const [adresses, setAdresses] = React.useState([]);
    const [batimentGroupe, setBatimentGroupe] = React.useState([]);
    const [batiments, setBatiments] = React.useState([]);
    const [batimentGroupeRnc, setBatimentGroupeRnc] = React.useState([]);
    const [batimentGroupeSyntheseProprieteUsage, setBatimentGroupeSyntheseProprieteUsage] = React.useState([]);
    const proprietaire = batimentGroupeSyntheseProprieteUsage;
    const [parcelles, setParcelles] = React.useState([]);
    const [dpes, setDpes] = React.useState([]);
    const [etab, setEtab] = React.useState([]);
    const [batimentGroupeAdresse, setBatimentGroupeAdresse] = React.useState([]);
    const [batimentConstruction, setBatimentConstruction] = React.useState([]);
    const [proprietaires, setProprietaires] = React.useState([]);
    const [rnc, setRnc] = React.useState([]);

    const [mapsForCsv, setMapsForCsv] = React.useState(null);

    // ── Guard : empêche de lancer deux fois le même appel initial ─────────────
    const fetchInitiated = React.useRef({
        adresses: false, batimentGroupe: false, batiments: false,
        batimentGroupeRnc: false, prop: false, parcelles: false,
        dpes: false, etab: false,
        batimentGroupeAdresse: false, batimentConstruction: false,
        proprietaires: false, rnc: false,
    });

    // ── Index de l'étape courante dans la séquence de chargement ─────────────
    const seqStep = React.useRef(0);

    // ── Annulation : token incrémenté à chaque cancel pour invalider les callbacks ──
    const cancelTokenRef  = React.useRef(0);
    const isCancelledRef  = React.useRef(false);

    // ── Données traitées ──────────────────────────────────────────────────────
    const [csvData, setCsvData] = React.useState(null);

    // ── Progression du traitement (0-100, null = pas en cours) ───────────────
    const [processingProgress, setProcessingProgress] = React.useState(null);
    const processingTimerRef = React.useRef(null);

    // ── Erreur de traitement (jointure / buildCsvData) ────────────────────────
    const [processingError, setProcessingError] = React.useState(null); // { message, stack }

    // ── État de progression par source (pour le LoadingPanel) ─────────────────
    const sourcesWithState = SOURCES_CONFIG.map(s => ({
        ...s,
        value: {
            etab: loadingEtab,
            Dpes: loadingDpes,
            BdnbProp: loadingProp,
            BdnbProprietaires: loadingProprietaires,
            bdnb: loadingBatiments,
            BdnbBatimentGroupeRnc: loadingBatimentGroupeRnc,
            BdnbRnc: loadingRnc,
            batimentGroupe: loadingBatimentGroupe,
            BatimentGroupeAdresses: loadingAdresses,
            Parcelles: loadingParcelles,
            BatimentGroupeAdresse: loadingBatimentGroupeAdresse,
            BatimentConstruction: loadingBatimentConstruction,
        }[s.key],
        count: {
            etab: etab.length,
            Dpes: dpes.length,
            BdnbProp: batimentGroupeSyntheseProprieteUsage.length,
            BdnbProprietaires: proprietaires.length,
            bdnb: batiments.length,
            BdnbBatimentGroupeRnc: batimentGroupeRnc.length,
            BdnbRnc: rnc.length,
            batimentGroupe: batimentGroupe.length,
            BatimentGroupeAdresses: adresses.length,
            Parcelles: parcelles.length,
            BatimentGroupeAdresse: batimentGroupeAdresse.length,
            BatimentConstruction: batimentConstruction.length,
        }[s.key] ?? 0,
    }));

    // ── Pagination générique ──────────────────────────────────────────────────

    function applyPaginatedResults(results, limit, offset, setData, setLoadingFn, fetchNext, token) {
        if (token !== cancelTokenRef.current) return; // résultat périmé après annulation
        setData(prev => [...prev, ...results]);
        if (!DEV_LIMIT && results.length === limit) {
            setLoadingFn(p => p + 1);
            fetchNext(limit, offset + limit, token);
        } else setLoadingFn(-1);
    }

    // ── Gestionnaire d'erreur générique ───────────────────────────────────────

    function handleError(err) {
        const status = err?.response?.status;
        const serverMessage = err?.response?.data?.message;
        const message = serverMessage
            || (status === 409 ? 'Une extraction est déjà en cours. Veuillez patienter.' : null)
            || err?.message
            || 'Une erreur inattendue est survenue.';
        setError({status, message});
        setLoading(false);
        setProcessing(false);
        setLoadingAdresses(-1);
        setLoadingBatimentGroupe(-1);
        setLoadingBatiments(-1);
        setLoadingBatimentGroupeRnc(-1);
        setLoadingProp(-1);
        setLoadingParcelles(-1);
        setLoadingDpes(-1);
        setLoadingEtab(-1);
        setLoadingBatimentGroupeAdresse(-1);
        setLoadingBatimentConstruction(-1);
        setLoadingProprietaires(-1);
        setLoadingRnc(-1);
        fetchInitiated.current = {
            adresses: false, batimentGroupe: false, batiments: false,
            batimentGroupeRnc: false, prop: false, parcelles: false,
            dpes: false, etab: false,
            batimentGroupeAdresse: false, batimentConstruction: false,
            proprietaires: false, rnc: false,
        };
        seqStep.current = 0;
    }

    // ── Fonctions de chargement par source ────────────────────────────────────

    function getAdresses(l, o, token) {
        ExtractCtrl.getBatimentGroupeAdresses(filter, l, o,
            r => applyPaginatedResults(r, l, o, setAdresses, setLoadingAdresses, getAdresses, token),
            handleError);
    }

    function getBatimentGroupe(l, o, token) {
        ExtractCtrl.getBatimentGroupe(filter, l, o,
            r => applyPaginatedResults(r, l, o, setBatimentGroupe, setLoadingBatimentGroupe, getBatimentGroupe, token),
            handleError);
    }

    function getBatiments(l, o, token) {
        ExtractCtrl.getBatiments(filter, l, o,
            r => applyPaginatedResults(r, l, o, setBatiments, setLoadingBatiments, getBatiments, token),
            handleError);
    }

    function getBatimentGroupeRnc(l, o, token) {
        ExtractCtrl.getBatimentGroupeRnc(filter, l, o,
            r => applyPaginatedResults(r, l, o, setBatimentGroupeRnc, setLoadingBatimentGroupeRnc, getBatimentGroupeRnc, token),
            handleError);
    }

    function getProp(l, o, token) {
        ExtractCtrl.getBatimentGroupeSyntheseProprieteUsage(filter, l, o,
            r => applyPaginatedResults(r, l, o, setBatimentGroupeSyntheseProprieteUsage, setLoadingProp, getProp, token),
            handleError);
    }

    function getParcelles(l, o, token) {
        ExtractCtrl.getParcelles(filter, l, o,
            r => applyPaginatedResults(r, l, o, setParcelles, setLoadingParcelles, getParcelles, token),
            handleError);
    }

    function getDpes(l, o, token) {
        ExtractCtrl.getDpes(filter, l, o,
            r => applyPaginatedResults(r, l, o, setDpes, setLoadingDpes, getDpes, token),
            handleError);
    }

    function getEtab(l, o, token) {
        ExtractCtrl.getEtab(filter, l, o,
            r => applyPaginatedResults(r, l, o, setEtab, setLoadingEtab, getEtab, token),
            handleError);
    }

    function getBatimentGroupeAdresse(l, o, token) {
        ExtractCtrl.getBatimentGroupeAdresse(filter, l, o,
            r => applyPaginatedResults(r, l, o, setBatimentGroupeAdresse, setLoadingBatimentGroupeAdresse, getBatimentGroupeAdresse, token),
            handleError);
    }

    function getBatimentConstruction(l, o, token) {
        ExtractCtrl.getBatimentConstruction(filter, l, o,
            r => applyPaginatedResults(r, l, o, setBatimentConstruction, setLoadingBatimentConstruction, getBatimentConstruction, token),
            handleError);
    }

    function fetchProprietaires(l, o, token) {
        ExtractCtrl.getProp(filter, l, o,
            r => applyPaginatedResults(r, l, o, setProprietaires, setLoadingProprietaires, fetchProprietaires, token),
            handleError);
    }

    function fetchRnc(l, o, token) {
        ExtractCtrl.getRnc(filter, l, o,
            r => applyPaginatedResults(r, l, o, setRnc, setLoadingRnc, fetchRnc, token),
            handleError);
    }

    // ── Déclenchement initial de chaque source ────────────────────────────────

    React.useEffect(() => {
        if (!filter?.geo) return;
        isCancelledRef.current = false;
        setError(null);
        setMapsForCsv(null);
        setCsvData(null);
        setLoading(true);
        setProcessing(true);
        setAdresses([]);
        setBatimentGroupe([]);
        setBatiments([]);
        setBatimentGroupeRnc([]);
        setBatimentGroupeSyntheseProprieteUsage([]);
        setParcelles([]);
        setEtab([]);
        setDpes([]);
        setBatimentGroupeAdresse([]);
        setBatimentConstruction([]);
        setProprietaires([]);
        setRnc([]);
        fetchInitiated.current = {
            adresses: false, batimentGroupe: false, batiments: false,
            batimentGroupeRnc: false, prop: false, parcelles: false,
            dpes: false, etab: false,
            batimentGroupeAdresse: false, batimentConstruction: false,
            proprietaires: false, rnc: false,
        };
        seqStep.current = 0;
        // 0 = en attente (pas encore lancé), la séquence démarrera dans le second useEffect
        setLoadingAdresses(0);
        setLoadingBatimentGroupe(0);
        setLoadingBatiments(0);
        setLoadingBatimentGroupeRnc(0);
        setLoadingProp(0);
        setLoadingParcelles(0);
        setLoadingEtab(0);
        setLoadingDpes(0);
        setLoadingBatimentGroupeAdresse(0);
        setLoadingBatimentConstruction(0);
        setLoadingProprietaires(0);
        setLoadingRnc(0);
    }, [filter]);


    function getNbEtage(hauteurBat, annee) {
        let h = 2.5;
        if (annee < 1920) {
            h = 3.3;
        } else if (annee < 1948) {
            h = 3;
        } else if (annee < 1978) {
            h = 2.8;
        } else if (annee < 1983) {
            h = 2.6;
        } else {
            h = 2.5;
        }
        return Math.floor(hauteurBat / h);
    }


    function computeDpeGes(res, etiquetteDpe) {
        if (res.dpes.length > 0) {
            let notes = {}

            res.dpes?.forEach(d => {
                let n = d[etiquetteDpe];
                if (!notes[n]) {
                    notes[n] = 0;
                }
                notes[n]++;
            })
            let note = null;

            let n = null
            Object.keys(notes).forEach(d => {
                if (n === null) {
                    n = notes[d];
                    note = d;
                }
                if (notes[d] >= n) {
                    n = notes[d];
                    note = d;
                }
            })
            return note
        }
        return ""
    }

    // ── Colonnes contenant des tableaux JSON à aplatir en liste de strings ────
    const JSON_ARRAY_COLUMNS = new Set([
        'batiment_groupe_bdtopo_bat.l_nature',
        'batiment_groupe_bdtopo_bat.l_usage_1',
        'batiment_groupe_bdtopo_bat.l_usage_2',
        'batiment_groupe_bdtopo_bat.l_type_equipement',
        'batiment_groupe_rnc.l_nom_copro',
        'batiment_groupe_rnc.l_siret',
    ]);

    // ── Colonnes dont la valeur doit être normalisée en title case ────────────
    const NORMALIZE_TEXT_COLUMNS = new Set([
        'batiment_groupe_bdtopo_bat.l_nature',
        'batiment_groupe_bdtopo_bat.l_usage_1',
        'batiment_groupe_bdtopo_bat.l_usage_2',
        'batiment_groupe_bdtopo_bat.l_type_equipement',
        'batiment_groupe_bdtopo_bat.usage_niveau_1_txt',
        'etab.derniernumerovoieetablissement',
        'etab.denominationusuelle2unitelegale',
        'etab.enseigne2etablissement',
        'etab.denominationusuelle3unitelegale',
    ]);

    /**
     * Convertit une chaîne en title case en gérant les caractères accentués français.
     * "TRAVAIL PROTÉGÉ" → "Travail Protégé"   "MAÇON" → "Maçon"
     */
    function normalizeText(value) {
        if (!value || typeof value !== 'string') return value;
        return value
            .toLowerCase()
            .replace(/(^|[\s\-\/])(\S)/gu, (_, sep, char) => sep + char.toUpperCase());
    }

    /**
     * Parse une valeur tableau au format PostgreSQL `{"val1","val2"}` ou JSON `["val1"]`.
     * Retourne un tableau de strings, ou [value] si le format n'est pas reconnu.
     */
    function parseJsonArray(value) {
        if (value == null) return [value];
        if (typeof value !== 'string') return [String(value)];
        const trimmed = value.trim();

        // ── Format JSON : ["val1","val2"] ─────────────────────────────────────
        if (trimmed.startsWith('[')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) return parsed.map(String);
            } catch { /* ignorer */
            }
        }

        // ── Format PostgreSQL : {"val1","val2"} ───────────────────────────────
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
            const inner = trimmed.slice(1, -1);
            if (inner.length === 0) return [];
            const results = [];
            // Regex : chaîne entre guillemets ou valeur sans virgule
            const re = /"((?:[^"\\]|\\.)*)"|([^,]+)/g;
            let m;
            while ((m = re.exec(inner)) !== null) {
                // groupe 1 = valeur entre guillemets, groupe 2 = valeur brute
                const val = m[1] !== undefined
                    ? m[1].replace(/\\"/g, '"').replace(/\\\\/g, '\\')
                    : m[2].trim();
                if (val !== 'NULL' && val !== '') results.push(val);
            }
            return results.length ? results : [inner];
        }

        return [trimmed];
    }

    React.useEffect(() => {
        // ── Séquence ordonnée des sources à charger ───────────────────────────
        const sequence = [
            {loading: loadingAdresses, setLoading: setLoadingAdresses, fetchFn: getAdresses, key: 'adresses'},
            {
                loading: loadingBatimentConstruction,
                setLoading: setLoadingBatimentConstruction,
                fetchFn: getBatimentConstruction,
                key: 'batimentConstruction'
            },
            {
                loading: loadingBatimentGroupe,
                setLoading: setLoadingBatimentGroupe,
                fetchFn: getBatimentGroupe,
                key: 'batimentGroupe'
            },
            {
                loading: loadingBatimentGroupeAdresse,
                setLoading: setLoadingBatimentGroupeAdresse,
                fetchFn: getBatimentGroupeAdresse,
                key: 'batimentGroupeAdresse'
            },
            {
                loading: loadingBatimentGroupeRnc,
                setLoading: setLoadingBatimentGroupeRnc,
                fetchFn: getBatimentGroupeRnc,
                key: 'batimentGroupeRnc'
            },
            {loading: loadingBatiments, setLoading: setLoadingBatiments, fetchFn: getBatiments, key: 'batiments'},
            {loading: loadingDpes, setLoading: setLoadingDpes, fetchFn: getDpes, key: 'dpes'},
            {loading: loadingEtab, setLoading: setLoadingEtab, fetchFn: getEtab, key: 'etab'},
            {loading: loadingParcelles, setLoading: setLoadingParcelles, fetchFn: getParcelles, key: 'parcelles'},
            {loading: loadingProp, setLoading: setLoadingProp, fetchFn: getProp, key: 'prop'},
            {
                loading: loadingProprietaires,
                setLoading: setLoadingProprietaires,
                fetchFn: fetchProprietaires,
                key: 'proprietaires'
            },
            {loading: loadingRnc, setLoading: setLoadingRnc, fetchFn: fetchRnc, key: 'rnc'},
        ];

        const allDone = sequence.every(s => s.loading === -1);

        if (!allDone) {
            const step = seqStep.current;
            if (step < sequence.length) {
                const current = sequence[step];
                if (current.loading === 0) {
                    // Étape en attente → la démarrer
                    current.setLoading(1);
                } else if (current.loading === 1 && !fetchInitiated.current[current.key]) {
                    // Étape prête → lancer le premier appel
                    fetchInitiated.current[current.key] = true;
                    current.fetchFn(LIMIT, 0, cancelTokenRef.current);
                } else if (current.loading === -1) {
                    // Étape terminée → passer à la suivante
                    seqStep.current += 1;
                    if (seqStep.current < sequence.length) {
                        sequence[seqStep.current].setLoading(1);
                    }
                }
                // Si loading > 1 : pagination en cours, on attend
            }
            return;
        }

        setLoading(false);
        // setProcessing(false) sera appelé après la construction du CSV
    }, [loadingAdresses, loadingBatimentGroupe, loadingBatiments, loadingBatimentGroupeRnc, loadingProp, loadingParcelles, loadingDpes, loadingEtab, loadingBatimentGroupeAdresse, loadingBatimentConstruction, loadingProprietaires, loadingRnc]);

    // ── Helper générique : construit un index { clé → [indices] } ────────────────
    function buildIndexMap(data, key) {
        const map = {};
        data.forEach((item, i) => {
            const val = item[key];
            if (val) {
                if (!map[val]) map[val] = [];
                map[val].push(i);
            }
        });
        return map;
    }


    // ── adresses (BAN) ────────────────────────────────────────────────────────
    // cle_interop_adr | batiment_construction_id | batiment_groupe_id
    function extractAdressesMap() {
        return {
            adresseBanMap: buildIndexMap(adresses, 'cle_interop_adr'),
            adresseBatimentMap: buildIndexMap(adresses, 'batiment_construction_id'),
            adresseBatimentGroupeMap: buildIndexMap(adresses, 'batiment_groupe_id'),
        };
    }

    // ── batiment_groupe ───────────────────────────────────────────────────────
    // batiment_groupe_id (clé unique par entrée)
    function extractBatimentGroupeMap() {
        return {
            batimentGroupeMap: buildIndexMap(batimentGroupe, 'batiment_groupe_id'),
        };
    }

    // ── batiment_groupe_bdtopo_bat ────────────────────────────────────────────
    // batiment_groupe_id
    function extractBatimentsMap() {
        return {
            batimentsMap: buildIndexMap(batiments, 'batiment_groupe_id'),
        };
    }

    // ── batiment_groupe_rnc ───────────────────────────────────────────────────
    // batiment_groupe_id
    function extractBatimentGroupeRncMap() {
        return {
            batimentGroupeRncMap: buildIndexMap(batimentGroupeRnc, 'batiment_groupe_id'),
        };
    }

    // ── batiment_groupe_synthese_propriete_usage (= proprietaire) ─────────────
    // batiment_groupe_id
    function extractProprietaireMap() {
        return {
            proprietaireMap: buildIndexMap(proprietaire, 'batiment_groupe_id'),
        };
    }

    // ── parcelles ─────────────────────────────────────────────────────────────
    // batiment_groupe_id (N-M) | parcelle_id
    function extractParcellesMap() {
        return {
            parcellesByBatimentGroupeMap: buildIndexMap(parcelles, 'batiment_groupe_id'),
            parcellesByParcelleIdMap: buildIndexMap(parcelles, 'parcelle_id'),
        };
    }

    // ── dpes ──────────────────────────────────────────────────────────────────
    // identifiant_ban = cle_interop_adr côté DPE (lien BAN → plusieurs DPE/adresse)
    function extractDpesMap() {
        return {
            dpesByCleInteropMap: buildIndexMap(dpes, 'identifiant_ban'),
        };
    }

    // ── etab ──────────────────────────────────────────────────────────────────
    // siret (clé établissement) | siren (unité légale) | cle_interop_adr (BAN calculé)
    function extractEtabMap() {
        return {
            etabBySiretMap: buildIndexMap(etab, 'siret'),
            etabBySirenMap: buildIndexMap(etab, 'siren'),
            etabByCleInteropMap: buildIndexMap(etab, 'cle_interop_adr'),
        };
    }

    // ── batiment_groupe_adresse ───────────────────────────────────────────────
    // batiment_groupe_id
    function extractBatimentGroupeAdresseMap() {
        return {
            batimentGroupeAdresseMap: buildIndexMap(batimentGroupeAdresse, 'batiment_groupe_id'),
        };
    }

    // ── batiment_construction ─────────────────────────────────────────────────
    // batiment_construction_id | batiment_groupe_id (1 groupe → N constructions)
    function extractBatimentConstructionMap() {
        return {
            batimentConstructionByIdMap: buildIndexMap(batimentConstruction, 'batiment_construction_id'),
            batimentConstructionByGroupeIdMap: buildIndexMap(batimentConstruction, 'batiment_groupe_id'),
        };
    }

    // ── proprietaires ─────────────────────────────────────────────────────────
    // batiment_groupe_id (N-M) | personne_id
    function extractProprietairesMap() {
        return {
            proprietairesByGroupeIdMap: buildIndexMap(proprietaires, 'batiment_groupe_id'),
            proprietairesByPersonneIdMap: buildIndexMap(proprietaires, 'personne_id'),
        };
    }

    // ── rnc ───────────────────────────────────────────────────────────────────
    // numerodimmatriculation (clé copropriété) | siretdurepresentantlegal
    function extractRncMap() {
        return {
            rncByNumerodMap: buildIndexMap(rnc, 'numerodimmatriculation'),
            //    rncBySiretMap:   buildIndexMap(rnc, 'siretdurepresentantlegal'),
        };
    }

    // ── Calcul de tous les mappings une fois que tout est chargé ─────────────
    React.useEffect(() => {
        if (!loading && filter?.geo && !isCancelledRef.current) {
            const maps = {
                ...extractAdressesMap(),
                ...extractBatimentGroupeMap(),
                ...extractBatimentsMap(),
                ...extractProprietaireMap(),
                ...extractParcellesMap(),
                ...extractDpesMap(),
                ...extractEtabMap(),
                ...extractBatimentGroupeAdresseMap(),
                ...extractBatimentConstructionMap(),
                ...extractProprietairesMap(),
                ...extractRncMap(),
                ...extractBatimentGroupeRncMap(),
            };
            setMapsForCsv(maps);
        }
    }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Construction du CSV agrégé ────────────────────────────────────────────
    /**
     * Construit { rows, meta } à partir des maps d'index et du mode géo sélectionné.
     *
     * Clé pivot par mode :
     *   geom_groupe   → batiment_groupe_id
     *   geom_cstr     → batiment_construction_id
     *   latlon        → batiment_construction_id  (+ lat/lon depuis geom_adresse)
     *   geom_adresse  → cle_interop_adr
     *   geom_parcelle → parcelle_id
     */
    function buildCsvData(mode, maps) {
        if (!maps) return null;

        const rowsMap = {};  // { rowId → { colKey → string | string[] } }
        const metaInfo = {};  // { colKey → { isNum, values: Set<string> } }

        // ── Micro-helpers ─────────────────────────────────────────────────────

        const getItems = (src, indices) =>
            indices?.length ? indices.map(i => src[i]).filter(Boolean) : [];

        function setVal(row, colKey, rawVal) {
            const v = (rawVal == null || rawVal === '') ? '' : String(rawVal);
            if (!metaInfo[colKey]) metaInfo[colKey] = {isNum: true, values: new Set()};
            metaInfo[colKey].values.add(v);
            if (v !== '' && isNaN(Number(v))) metaInfo[colKey].isNum = false;
            const cur = row[colKey];
            if (cur === undefined) row[colKey] = [v];
            else cur.push(v);
        }

        function addItem(row, prefix, item) {
            if (!item) return;
            for (const [field, rawVal] of Object.entries(item)) {
                const colKey = `${prefix}.${field}`;
                let vals = JSON_ARRAY_COLUMNS.has(colKey) ? parseJsonArray(rawVal) : rawVal;
                const norm = NORMALIZE_TEXT_COLUMNS.has(colKey);
                if (Array.isArray(vals)) vals.forEach(v => setVal(row, colKey, norm ? normalizeText(v) : v));
                else setVal(row, colKey, norm ? normalizeText(vals) : vals);
            }
        }

        const addItems = (row, prefix, src, indices) =>
            getItems(src, indices).forEach(item => addItem(row, prefix, item));

        /** Extrait [lat, lon] depuis une géométrie GeoJSON Point sérialisée ou objet */
        function latLonFromGeom(geom) {
            if (!geom) return [null, null];
            try {
                const g = typeof geom === 'string' ? JSON.parse(geom) : geom;
                if (g?.type === 'Point' && Array.isArray(g.coordinates) && g.coordinates.length >= 2)
                    return [g.coordinates[1], g.coordinates[0]]; // [lat, lon]
            } catch { /* ignore */
            }
            return [null, null];
        }

        /**
         * Calcule le centroïde (centre de la bbox) d'une géométrie GeoJSON.
         * Supporte : Point, Polygon, MultiPolygon, LineString, GeometryCollection
         * (string GeoJSON ou objet). Retourne [lat, lon] ou [null, null].
         */
        function centroidFromGeom(geom) {
            if (!geom) return [null, null];
            try {
                const g = typeof geom === 'string' ? JSON.parse(geom) : geom;
                if (!g?.type) return [null, null];

                function flatCoords(geo) {
                    switch (geo.type) {
                        case 'Point':
                            return [geo.coordinates];
                        case 'MultiPoint':
                        case 'LineString':
                            return geo.coordinates;
                        case 'MultiLineString':
                            return geo.coordinates.flat(1);
                        case 'Polygon':
                            return geo.coordinates[0]; // anneau extérieur
                        case 'MultiPolygon':
                            return geo.coordinates.flatMap(poly => poly[0]);
                        case 'GeometryCollection':
                            return geo.geometries.flatMap(flatCoords);
                        default:
                            return [];
                    }
                }

                const pts = flatCoords(g);
                if (!pts.length) return [null, null];

                let minLon = Infinity, maxLon = -Infinity;
                let minLat = Infinity, maxLat = -Infinity;
                for (const [lon, lat] of pts) {
                    if (lon < minLon) minLon = lon;
                    if (lon > maxLon) maxLon = lon;
                    if (lat < minLat) minLat = lat;
                    if (lat > maxLat) maxLat = lat;
                }
                return [(minLat + maxLat) / 2, (minLon + maxLon) / 2];
            } catch { /* ignore */
            }
            return [null, null];
        }


        // computeMetaColumns est importé depuis '../utils/metaColumnsUtils'.

        /** Crée ou récupère une ligne et initialise ses champs meta.* */
        function getOrCreateRow(rowId, metaFields) {
            if (!rowsMap[rowId]) rowsMap[rowId] = {};
            const row = rowsMap[rowId];
            Object.entries(metaFields).forEach(([k, v]) => setVal(row, `meta.${k}`, v));
            return row;
        }

        // ── Hub central : alimente une ligne depuis un batiment_groupe_id ─────
        function fillGroupeData(row, bg_id) {
            addItems(row, 'batiment_groupe', batimentGroupe, maps.batimentGroupeMap?.[bg_id]);
            addItems(row, 'batiment_groupe_bdtopo_bat', batiments, maps.batimentsMap?.[bg_id]);
            addItems(row, 'batiment_groupe_adresse', batimentGroupeAdresse, maps.batimentGroupeAdresseMap?.[bg_id]);
            addItems(row, 'batiment_groupe_synthese_propriete_usage', proprietaire, maps.proprietaireMap?.[bg_id]);
            addItems(row, 'parcelles', parcelles, maps.parcellesByBatimentGroupeMap?.[bg_id]);
            addItems(row, 'proprietaires', proprietaires, maps.proprietairesByGroupeIdMap?.[bg_id]);

            // batiment_groupe_rnc → cascade vers le RNC open-data via l_siret
            getItems(batimentGroupeRnc, maps.batimentGroupeRncMap?.[bg_id]).forEach(item => {
                addItem(row, 'batiment_groupe_rnc', item);

                parseJsonArray(item.numero_immat_principal).forEach(numero_immat_principal =>
                    addItems(row, 'rnc', rnc, maps.rncByNumerodMap?.[numero_immat_principal])
                );
            });

            // adresses → DPEs + établissements (identifiant_ban = cle_interop_adr)
            getItems(adresses, maps.adresseBatimentGroupeMap?.[bg_id]).forEach(adr => {
                addItem(row, 'adresses', adr);
                const cle = adr.cle_interop_adr;
                if (cle) {
                    addItems(row, 'dpes', dpes, maps.dpesByCleInteropMap?.[cle]);
                    addItems(row, 'etab', etab, maps.etabByCleInteropMap?.[cle]);
                }
            });
        }

        // ── Construction par mode pivot ───────────────────────────────────────

        if (mode === 'geom_groupe') {
            // 1 ligne par batiment_groupe_id
            const bgIds = new Set([
                ...Object.keys(maps.batimentGroupeMap ?? {}),
                ...Object.keys(maps.batimentConstructionByGroupeIdMap ?? {}),
            ]);
            bgIds.forEach(bg_id => {
                const row = getOrCreateRow(bg_id, {index: bg_id, batiment_groupe_id: bg_id});
                addItems(row, 'batiment_construction', batimentConstruction, maps.batimentConstructionByGroupeIdMap?.[bg_id]);
                fillGroupeData(row, bg_id);
                // Centroïde de l'emprise du groupe → meta.latitude / meta.longitude
                for (const bg of getItems(batimentGroupe, maps.batimentGroupeMap?.[bg_id])) {
                    const [lat, lon] = centroidFromGeom(bg.geom_groupe);
                    if (lat !== null) {
                        setVal(row, 'meta.latitude', lat);
                        setVal(row, 'meta.longitude', lon);
                        break;
                    }
                }
            });

        } else if (mode === 'latlon') {
            // 1 ligne par cle_interop_adr (adresse BAN = clé principale)
            Object.keys(maps.adresseBanMap ?? {}).forEach(cle => {
                const adrItems = getItems(adresses, maps.adresseBanMap?.[cle]);
                const firstAdr = adrItems[0];
                const bc_id = firstAdr?.batiment_construction_id;
                const bg_id = firstAdr?.batiment_groupe_id;
                const row = getOrCreateRow(cle, {
                    index: cle,
                    cle_interop_adr: cle,
                    ...(bc_id ? {batiment_construction_id: bc_id} : {}),
                    ...(bg_id ? {batiment_groupe_id: bg_id} : {}),
                });
                adrItems.forEach(adr => addItem(row, 'adresses', adr));

                // lat/lon depuis la géométrie BAN de l'adresse
                for (const adr of adrItems) {
                    const [lat, lon] = latLonFromGeom(adr.geom_adresse);
                    if (lat !== null) {
                        setVal(row, 'meta.latitude', lat);
                        setVal(row, 'meta.longitude', lon);
                        break;
                    }
                }
                // Fallback : centroïde de la géométrie de construction
                if (!row['meta.latitude'] && bc_id) {
                    for (const bc of getItems(batimentConstruction, maps.batimentConstructionByIdMap?.[bc_id])) {
                        const [lat, lon] = centroidFromGeom(bc.geom_cstr);
                        if (lat !== null) {
                            setVal(row, 'meta.latitude', lat);
                            setVal(row, 'meta.longitude', lon);
                            break;
                        }
                    }
                }
                if (bc_id) addItems(row, 'batiment_construction', batimentConstruction, maps.batimentConstructionByIdMap?.[bc_id]);
                if (bg_id) fillGroupeData(row, bg_id);
            });

        } else if (mode === 'geom_cstr') {
            // 1 ligne par batiment_construction_id
            Object.keys(maps.batimentConstructionByIdMap ?? {}).forEach(bc_id => {
                const bcItems = getItems(batimentConstruction, maps.batimentConstructionByIdMap?.[bc_id]);
                const bg_id = bcItems[0]?.batiment_groupe_id;
                const row = getOrCreateRow(bc_id, {
                    index: bc_id,
                    batiment_construction_id: bc_id,
                    ...(bg_id ? {batiment_groupe_id: bg_id} : {}),
                });
                addItems(row, 'batiment_construction', batimentConstruction, maps.batimentConstructionByIdMap?.[bc_id]);
                if (bg_id) fillGroupeData(row, bg_id);

                // Centroïde de la géométrie de construction
                if (!row['meta.latitude']) {
                    for (const bc of getItems(batimentConstruction, maps.batimentConstructionByIdMap?.[bc_id])) {
                        const [lat, lon] = centroidFromGeom(bc.geom_cstr);
                        if (lat !== null) {
                            setVal(row, 'meta.latitude', lat);
                            setVal(row, 'meta.longitude', lon);
                            break;
                        }
                    }
                }
            });

        } else if (mode === 'geom_adresse') {
            // 1 ligne par cle_interop_adr
            Object.keys(maps.adresseBanMap ?? {}).forEach(cle => {
                const adrItems = getItems(adresses, maps.adresseBanMap?.[cle]);
                const firstAdr = adrItems[0];
                const bc_id = firstAdr?.batiment_construction_id;
                const bg_id = firstAdr?.batiment_groupe_id;
                const row = getOrCreateRow(cle, {
                    index: cle,
                    cle_interop_adr: cle,
                    ...(bc_id ? {batiment_construction_id: bc_id} : {}),
                    ...(bg_id ? {batiment_groupe_id: bg_id} : {}),
                });
                adrItems.forEach(adr => addItem(row, 'adresses', adr));

                // lat/lon depuis geom_adresse
                if (firstAdr?.geom_adresse) {
                    const [lat, lon] = latLonFromGeom(firstAdr.geom_adresse);
                    if (lat !== null) {
                        setVal(row, 'meta.latitude', lat);
                        setVal(row, 'meta.longitude', lon);
                    }
                }
                addItems(row, 'dpes', dpes, maps.dpesByCleInteropMap?.[cle]);
                addItems(row, 'etab', etab, maps.etabByCleInteropMap?.[cle]);

                if (bc_id) addItems(row, 'batiment_construction', batimentConstruction, maps.batimentConstructionByIdMap?.[bc_id]);
                if (bg_id) fillGroupeData(row, bg_id);
            });

        } else if (mode === 'geom_parcelle') {
            // 1 ligne par parcelle_id
            Object.keys(maps.parcellesByParcelleIdMap ?? {}).forEach(parcelle_id => {
                const pItems = getItems(parcelles, maps.parcellesByParcelleIdMap?.[parcelle_id]);
                const bg_id = pItems[0]?.batiment_groupe_id;
                const row = getOrCreateRow(parcelle_id, {
                    index: parcelle_id,
                    parcelle_id,
                    ...(bg_id ? {batiment_groupe_id: bg_id} : {}),
                });
                pItems.forEach(p => addItem(row, 'parcelles', p));
                // Centroïde de la géométrie de la parcelle → meta.latitude / meta.longitude
                for (const p of pItems) {
                    const [lat, lon] = centroidFromGeom(p.geom_parcelle);
                    if (lat !== null) {
                        setVal(row, 'meta.latitude', lat);
                        setVal(row, 'meta.longitude', lon);
                        break;
                    }
                }
                if (bg_id) {
                    addItems(row, 'batiment_construction', batimentConstruction, maps.batimentConstructionByGroupeIdMap?.[bg_id]);
                    fillGroupeData(row, bg_id);
                }
            });
        }

        // ── Post-traitement : colonnes meta synthétiques ──────────────────────
        Object.values(rowsMap).forEach(row => {


            // ── Colonne calculée : other.proprietaires_contact ───────────────
            // Format : "siren[0];denomination[0]|siren[1];denomination[1]|…"
            // Itère sur le tableau le plus grand pour ne perdre aucune valeur.
            {
                const toArr = v => v == null ? [] : Array.isArray(v) ? v : [v];
                const sirens = toArr(row['proprietaires.siren']);
                const denoms = toArr(row['proprietaires.denomination']);
                const len = Math.max(sirens.length, denoms.length);
                const pairs = Array.from({length: len}, (_, i) => `${sirens[i] ?? ''};${denoms[i] ?? ''}`);
                pairs.forEach(p => setVal(row, 'other.proprietaires_contact', p));
            }

            // ── Colonne calculée : other.etab_contact ─────────────────────────
            // Format : "siren[0];denomination[0];activite[0];etat[0]|siren[1];…"
            // Itère sur le tableau le plus grand pour ne perdre aucune valeur.
            {
                const toArr = v => v == null ? [] : Array.isArray(v) ? v : [v];
                const etabSirens = toArr(row['etab.siren']);
                const etabDenoms = toArr(row['etab.denominationunitelegale']);
                const etabActivites = toArr(row['etab.activiteprincipaleunitelegale']);
                const etabEtats = toArr(row['etab.etatadministratifetablissement']);

                const len = Math.max(etabSirens.length, etabDenoms.length, etabActivites.length, etabEtats.length);
                const pairs = Array.from({length: len}, (_, i) =>
                    `${etabSirens[i] ?? ''};${etabDenoms[i] ?? ''};${etabActivites[i] ?? ''};${etabEtats[i] ?? ''}`
                );
                pairs.forEach(p => setVal(row, 'other.etab_contact', p));
            }

            const synth = computeMetaColumns(row) ?? {};
            if (synth.nb_etages_estimes_adresse) setVal(row, 'meta.nb_etages_estimes_adresse', synth.nb_etages_estimes_adresse);
            if (synth.surface_utile_calc_adresse) setVal(row, 'meta.surface_utile_calc_adresse', synth.surface_utile_calc_adresse);

            if (synth.nb_etages_estimes_construction) setVal(row, 'meta.nb_etages_estimes_construction', synth.nb_etages_estimes_construction);
            if (synth.surface_utile_calc_construction) setVal(row, 'meta.surface_utile_calc_construction', synth.surface_utile_calc_construction);


            if (synth.nb_etages_reel_groupe) setVal(row, 'meta.nb_etages_reel_groupe', synth.nb_etages_reel_groupe);
            if (synth.nb_etages_estimes_groupe) setVal(row, 'meta.nb_etages_estimes_groupe', synth.nb_etages_estimes_groupe);
            if (synth.surface_utile_calc_groupe) setVal(row, 'meta.surface_utile_calc_groupe', synth.surface_utile_calc_groupe);
            if (synth.surface_utile_reel_groupe) setVal(row, 'meta.surface_utile_reel_groupe', synth.surface_utile_reel_groupe);

            if (synth.nb_etages_reel_parcelle) setVal(row, 'meta.nb_etages_reel_parcelle', synth.nb_etages_reel_parcelle);
            if (synth.nb_etages_estimes_parcelle) setVal(row, 'meta.nb_etages_estimes_parcelle', synth.nb_etages_estimes_parcelle);
            if (synth.surface_utile_calc_parcelle) setVal(row, 'meta.surface_utile_calc_parcelle', synth.surface_utile_calc_parcelle);
            if (synth.surface_utile_reel_parcelle) setVal(row, 'meta.surface_utile_reel_parcelle', synth.surface_utile_reel_parcelle);


            if (synth.nb_etages_estimes) setVal(row, 'meta.nb_etages_estimes', synth.nb_etages_estimes);
            if (synth.surface_utile_calc) setVal(row, 'meta.surface_utile_calc', synth.surface_utile_calc);
            // DPE / GES médian et moyen — toujours renseignés ('N' si aucune donnée)
            setVal(row, 'meta.libelle_adresse', synth.libelle_adresse);
            setVal(row, 'meta.etiquette_dpe_median', synth.etiquette_dpe_median);
            setVal(row, 'meta.etiquette_dpe_moyen', synth.etiquette_dpe_moyen);
            setVal(row, 'meta.etiquette_ges_median', synth.etiquette_ges_median);
            setVal(row, 'meta.etiquette_ges_moyen', synth.etiquette_ges_moyen);
        });

        // ── Colonnes meta synthétiques toujours présentes (même si vides) ───
        [
            'other.proprietaires_contact',
            'other.etab_contact',
            'meta.libelle_adresse',
            'meta.etiquette_dpe_median',
            'meta.etiquette_dpe_moyen',
            'meta.etiquette_ges_median',
            'meta.etiquette_ges_moyen',
        ].forEach(k => {
            if (!metaInfo[k]) metaInfo[k] = {isNum: false, values: new Set()};
        });

        // ── Finaliser la méta (meta.* en premier, puis les sources) ──────────
        const meta = {};
        [...Object.keys(metaInfo).filter(k => k.startsWith('meta.')),
            ...Object.keys(metaInfo).filter(k => !k.startsWith('meta.'))]
            .forEach(colKey => {
                const info = metaInfo[colKey];
                meta[colKey] = {
                    type: info.isNum && info.values.size > 0 ? 'number' : 'string',
                    values: [...info.values].slice(0, 200),
                };
            });

        return {rows: rowsMap, meta};
    }

    // ── Reconstruction du CSV quand les maps ou le mode changent ─────────────
    React.useEffect(() => {
        if (!mapsForCsv || !filter?.geo) return;

        // 1er render : afficher la barre de chargement avant le calcul lourd
        setProcessing(true);
        setTableReady(false);
        setProcessingProgress(0);

        // Animation progressive 0 → 90% pendant le traitement
        let prog = 0;
        if (processingTimerRef.current) clearInterval(processingTimerRef.current);
        processingTimerRef.current = setInterval(() => {
            prog = Math.min(90, prog + Math.random() * 8 + 2);
            setProcessingProgress(Math.round(prog));
            if (prog >= 90) {
                clearInterval(processingTimerRef.current);
                processingTimerRef.current = null;
            }
        }, 120);

        // 2e render (tick suivant) : construire et stocker le CSV
        const timer = setTimeout(() => {
            try {
                const csv = buildCsvData(geoMode, mapsForCsv);
                if (processingTimerRef.current) {
                    clearInterval(processingTimerRef.current);
                    processingTimerRef.current = null;
                }
                setProcessingProgress(100);
                setCsvData(csv);
                setProcessing(false);
                setTimeout(() => setProcessingProgress(null), 400);
                console.log('[useExtractData] CSV prêt :', Object.keys(csv.rows).length, 'lignes,', Object.keys(csv.meta).length, 'colonnes, mode =', geoMode);
            } catch (err) {
                if (processingTimerRef.current) {
                    clearInterval(processingTimerRef.current);
                    processingTimerRef.current = null;
                }
                setProcessingProgress(null);
                setProcessing(false);
                setProcessingError({ message: err?.message ?? String(err), stack: err?.stack ?? '' });
                console.error('[useExtractData] Erreur lors du traitement (jointure) :', err);
            }
        }, 0);

        // Si le mode change à nouveau avant la fin, on annule le timer en cours
        return () => {
            clearTimeout(timer);
            if (processingTimerRef.current) {
                clearInterval(processingTimerRef.current);
                processingTimerRef.current = null;
            }
        };
    }, [mapsForCsv, geoMode]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (!csvData) {
            setTableReady(false);
            return;
        }
        const t = setTimeout(() => setTableReady(true), 50);
        return () => clearTimeout(t);
    }, [csvData]);

    // ── Annulation du chargement en cours ─────────────────────────────────────
    function cancel() {
        isCancelledRef.current = true;
        cancelTokenRef.current += 1;   // invalide tous les callbacks en vol
        setError(null);
        setLoading(false);
        setProcessing(false);
        setTableReady(false);
        setCsvData(null);
        setMapsForCsv(null);
        setAdresses([]);
        setBatimentGroupe([]);
        setBatiments([]);
        setBatimentGroupeRnc([]);
        setBatimentGroupeSyntheseProprieteUsage([]);
        setParcelles([]);
        setDpes([]);
        setEtab([]);
        setBatimentGroupeAdresse([]);
        setBatimentConstruction([]);
        setProprietaires([]);
        setRnc([]);
        setLoadingAdresses(-1);
        setLoadingBatimentGroupe(-1);
        setLoadingBatiments(-1);
        setLoadingBatimentGroupeRnc(-1);
        setLoadingProp(-1);
        setLoadingParcelles(-1);
        setLoadingDpes(-1);
        setLoadingEtab(-1);
        setLoadingBatimentGroupeAdresse(-1);
        setLoadingBatimentConstruction(-1);
        setLoadingProprietaires(-1);
        setLoadingRnc(-1);
        fetchInitiated.current = {
            adresses: false, batimentGroupe: false, batiments: false,
            batimentGroupeRnc: false, prop: false, parcelles: false,
            dpes: false, etab: false,
            batimentGroupeAdresse: false, batimentConstruction: false,
            proprietaires: false, rnc: false,
        };
        seqStep.current = 0;
    }

    return {csvData, loading, processing, processingProgress, processingError, setProcessingError, tableReady, sourcesWithState, rnc, error, cancel};
}
